import { computed, effect, inject, Injectable, Injector, signal, untracked } from '@angular/core';
import { MinesAutoRunner } from './mines-auto.runner';
import { NotificationService } from '@app/notification/notification.service';
import { GameStatus, MinesGame, MinesLoad, MineStatus } from '@models/mines.interface';
import { SessionService } from '@shared/session.service';
import { SocketService } from '@shared/socket.service';
import { UIService } from '@shared/ui.service';

@Injectable({
  providedIn: 'root',
})
export class MinesService {
  readonly GRID_SIZE = 25;
  readonly MIN_BET = 0.06;
  readonly MAX_BET = 100;
  private readonly HOUSE_EDGE = 0.09;

  mines = signal<number>(5);
  amount = signal<number>(0.1);
  multiplier = signal<number>(1);
  lastWinPayout = signal<number | null>(null);

  clicked = signal<number[]>([]);
  minesPosition = signal<number[]>([]);

  currentGame = signal<MinesGame>({
    grid: this.generateGrid(),
  });

  auto!: MinesAutoRunner;

  /**
   * Memoized login gate for the connection effect below: `update_level` pushes
   * rebuild the profile object on every bet, but this computed collapses it to
   * a boolean, so the effect only re-runs on real connect/login transitions —
   * not on every XP push (which used to emit a useless `load_mines` per bet).
   */
  private readonly profileReady = computed(() => !!this.session.profile());

  /**
   * Forward-looking estimate for the auto "Payout On Win" field: what you'd
   * collect if every pre-selected tile hits. Reuses the live payout formula so
   * it always matches what actually pays out (house edge included).
   */
  estimatedPayout = computed<number | null>(() => {
    const picks = this.auto.activePreselected().size;
    if (picks === 0) return null;
    return this.calculateTileMultiplier(this.GRID_SIZE, this.mines(), picks - 1) * this.amount();
  });

  constructor(private socketService: SocketService, private session: SessionService, private notificationsService: NotificationService, private uiService: UIService) {
    this.auto = new MinesAutoRunner(inject(Injector), this, this.socketService, this.session, this.notificationsService);

    // Connection-driven: re-runs whenever the socket connects or the profile
    // arrives, so a round survives a reload or a mid-game reconnect.
    effect(() => {
      if (this.socketService.connected() && this.profileReady() && untracked(() => this.auto.autoStatus() === 'idle' && this.currentGame()?.status !== 'active')) {
        this.loadMines();
      }
    });
  }

  async loadMines(): Promise<void> {
    try {
      const game = await this.socketService.emit<MinesLoad | null>('load_mines', undefined, { noErrorMessage: true });
      if (game) {
        const { grid, ...rest } = game;
        const transformedGrid = Object.keys(grid).map((position) => Number(position));
        // `results` is authoritative when the server sends it; otherwise recover
        // the revealed tiles from the grid itself.
        const clicked = game.results?.length ? game.results : transformedGrid.filter((key) => !!grid[key]);
        const mines = game.mines ?? this.mines();
        const value = game.value ?? this.amount();

        this.clicked.set([...clicked]);
        this.currentGame.set({ ...rest, grid: transformedGrid });
        this.multiplier.set(this.calculateTileMultiplier(game.grid_size ?? this.GRID_SIZE, mines, clicked.length - 1));
        this.mines.set(mines);
        this.amount.set(value);
      } else if (this.currentGame()?.status === 'active') {
        // We think a game is live but the server has none (stale state after a
        // reconnect) — clear it. A 'finished' board stays: it is only showing
        // results, and wiping it here closed the last auto round's reveal when
        // a profile push (e.g. update_level) re-ran the connection effect.
        this.resetBoard();
      }
    } catch {
      // silent — the effect retries on the next connect
    }
  }

  /**
   * @param silent suppress the socket error toast (the auto runner retries
   *   start_mines and reports one aggregate failure instead).
   */
  async placeBet(silent = false): Promise<boolean> {
    if (this.amount() < this.MIN_BET) {
      this.notificationsService.error(`Minimum bet is ${this.MIN_BET}.`, 'Mines');
      return false;
    }

    this.clicked.set([]);
    this.minesPosition.set([]);
    this.playSound('mines-click');

    // Capture the wallet once: the user can switch wallets during the ack window,
    // and the payload must name the wallet the server was told to debit.
    const balanceType = this.session.balanceType();
    const mines = Math.floor(this.mines());
    const amount = this.amount();

    const hash = await this.socketService.emit<string>(
      'start_mines',
      {
        mines,
        amount,
        grid_size: this.GRID_SIZE,
        balance: balanceType,
      },
      { noErrorMessage: silent },
    );

    this.currentGame.update((prev) => ({
      ...prev,
      status: 'active' as GameStatus,
      hash,
      balancetype: balanceType,
      grid_size: this.GRID_SIZE,
      mines,
      value: amount,
    }));
    this.notify('Game started!');
    return true;
  }

  /**
   * @param silent suppress the socket error toast. The promise still rejects on
   *   failure — the auto runner's cleanup paths depend on that.
   */
  async checkout(silent = false): Promise<void> {
    const game = await this.socketService.emit<MinesGame>('cashout_mines', undefined, {
      timeout: 5000,
      noErrorMessage: silent,
    });

    this.applyFinalizedGame(game, true);

    if (this.currentGame().won) {
      this.lastWinPayout.set(this.multiplier() * this.amount());
      this.playSound('mines-win');
    }
  }

  async checkMine(position: number): Promise<void> {
    this.playSound('mines-click');
    const ack = await this.socketService.emit<MinesGame | number>('select_mine', { tile: position });

    if (typeof ack !== 'number' && !this.isFinalizedGameAck(ack)) {
      console.error('[Mines] Unexpected select_mine response', ack);
      throw new Error('Unexpected select_mine response');
    }

    // The tile joins `clicked` either way so the board reveals it. A plain number
    // is a safe pick, and a finalized game with won=true means the server closed
    // the round on this pick — also safe, so it still earns its step. Only a
    // bust leaves the multiplier where it was.
    this.clicked.update((prev) => [...prev, position]);

    const won = typeof ack === 'number' || this.resolveWon(ack, this.clicked());

    if (won) {
      this.multiplier.set(this.calculateTileMultiplier(this.GRID_SIZE, this.mines(), this.clicked().length - 1));
    }

    if (typeof ack === 'number') {
      this.playSound('mines-diamond');
      return;
    }

    this.applyFinalizedGame(ack);

    if (won) {
      this.lastWinPayout.set(this.multiplier() * this.amount());
      this.playSound('mines-win');
    } else {
      this.lastWinPayout.set(null);
      this.playSound('mines-hit');
    }
  }

  /** SkinBet-only convenience: reveal a random unopened tile in manual play. */
  async pickRandomTile(): Promise<void> {
    if (this.currentGame().status !== 'active') return;
    if (this.auto.autoStatus() !== 'idle') return;

    const opened = new Set(this.clicked());
    const available = this.generateGrid().filter((tile) => !opened.has(tile));
    if (available.length === 0) return;

    await this.checkMine(available[Math.floor(Math.random() * available.length)]);
  }

  setBet(type: 'clear' | 'min' | 'max' | 'half' | 'double'): void {
    this.amount.update((prev) => {
      // The masked bet input writes a string into the signal, so normalize first.
      let next = Number(prev) || 0;
      switch (type) {
        case 'clear':
          next = 0;
          break;
        case 'min':
          next = this.MIN_BET;
          break;
        case 'max':
          next = Math.min(this.session.currentBalance() ?? 0, this.MAX_BET);
          break;
        case 'half':
          next = next / 2;
          break;
        case 'double':
          next = next * 2;
          break;
      }
      return Math.min(Math.round(next * 100) / 100, this.MAX_BET);
    });
  }

  resetBoard(): void {
    this.currentGame.update((prev) => ({
      grid: this.generateGrid(),
      // Keep the settings the next round inherits.
      ...(prev.grid_size && { grid_size: prev.grid_size }),
      ...(prev.mines && { mines: prev.mines }),
      ...(prev.value && { value: prev.value }),
    }));
    this.clicked.set([]);
    this.minesPosition.set([]);
    this.multiplier.set(1);
  }

  extractMinePositions(grid: number[]): number[] {
    return grid.map((value, index) => Number(value === MineStatus.HIT && index + 1)).filter(Boolean);
  }

  calculateTileMultiplier(gridSize: number, mines: number, solved: number): number {
    if (solved < 0) {
      return 1;
    }
    return ((1 - this.HOUSE_EDGE) * this.calculateBinomialCoefficient(gridSize, solved + 1)) / this.calculateBinomialCoefficient(gridSize - mines, solved + 1);
  }

  private applyFinalizedGame(game: MinesGame, cashout = false): void {
    const { grid, ...rest } = game;
    const won = this.resolveWon(game, this.clicked(), cashout);

    // An object response only ever describes a finished game; pin the status so
    // the board resolves even if the server omits it. `won` is pinned from the
    // resolver rather than left to the spread — an ack that omits the flag would
    // otherwise inherit the previous round's result (or record every win as a
    // loss while it is still undefined).
    this.currentGame.update((prev) => ({ ...prev, ...rest, won, status: 'finished' as GameStatus }));

    if (Array.isArray(grid)) {
      this.minesPosition.set([...this.extractMinePositions(grid)]);
    }
  }

  /**
   * `won` is authoritative when the server sends it. Some acks omit it, so fall
   * back to the revealed grid: the round is a win only if no picked tile is a
   * mine. A cashout ack with no grid is a win — the server rejects
   * `cashout_mines` unless the game is live and unbusted.
   */
  resolveWon(game: MinesGame, picked: number[], cashout = false): boolean {
    if (typeof game.won === 'boolean') return game.won;
    if (!Array.isArray(game.grid)) return cashout;

    const mines = new Set(this.extractMinePositions(game.grid));
    return !picked.some((tile) => mines.has(tile));
  }

  /**
   * A `select_mine` ack is either a plain number (safe tile, round continues) or
   * an object describing the finished game. Anything else means we can't tell
   * what the server did, so callers abort instead of guessing.
   */
  isFinalizedGameAck(ack: unknown): ack is MinesGame {
    if (!ack || typeof ack !== 'object' || Array.isArray(ack)) {
      return false;
    }
    return 'status' in ack || 'won' in ack || 'grid' in ack;
  }

  /**
   * Fire-and-forget, and fully muted while the auto runner drives the board:
   * awaiting audio would pace the emit loop, and a round ends every ~700ms.
   */
  private playSound(sound: string): void {
    if (this.auto && this.auto.autoStatus() !== 'idle') return;
    this.uiService.playSound(sound, { extension: 'mp3' });
  }

  private notify(message: string): void {
    if (this.auto && this.auto.autoStatus() !== 'idle') return;
    this.notificationsService.info(message, 'Mines', { timeout: 1500 });
  }

  private generateGrid(): number[] {
    return Array.from({ length: this.GRID_SIZE }, (_, index) => index + 1);
  }

  private calculateBinomialCoefficient(grid_size: number, solved: number): number {
    try {
      if (grid_size < solved) {
        return 0;
      }

      const numerator = this.generateRange(grid_size - solved + 1, grid_size).reduce((acc, curr) => acc * curr);
      const denominator = this.generateRange(1, solved).reduce((e, t) => e * t);

      return numerator / denominator;
    } catch (err) {
      console.error('[Mines] binomial coefficient failed', grid_size, solved, err);
      return 0;
    }
  }

  private generateRange(start: number, end: number): number[] {
    return [...Array(end - start + 1)].map((_, index) => start + index);
  }
}
